import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  AUTH_CHANGED_EVENT,
  clearAuthSession,
  getStoredUser,
  isAdminUser,
} from '../utils/auth';
import '../styles/Navbar.css';
import '../styles/Darktheme.css';
const MapModal = lazy(() => import('./MapModal'));

const Navbar = ({ onLogout, handleToggle, isToggled, children }) => {
  const [userRole, setUserRole] = useState('');
  const [userName, setUserName] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const syncUser = () => {
      const user = getStoredUser();
      setUserRole(user.perfil || '');
      setUserName(user.nome || user.username || user.perfil || 'Usuário');
    };

    syncUser();
    window.addEventListener(AUTH_CHANGED_EVENT, syncUser);
    return () => window.removeEventListener(AUTH_CHANGED_EVENT, syncUser);
  }, []);

  const handleSignOut = async () => {
    try {
      clearAuthSession();
      onLogout();
      navigate('/');
    } catch (error) {
      console.error(error);
    }
  };

  const dashboardItem = { path: '/user/dashboard', name: 'Dashboard' };
  const diariaItem = { path: '/user/diaria', name: 'Diária' };
  const suprimentoItem = { path: '/user/suprimento', name: 'Suprimento' };
  const frequenciaItem = { path: '/user/RH', name: 'RH' };
  const processosItem = { path: '/processos', name: 'Processos' };
  const formItem = {
    path: '/user/form',
    name: 'Formulário',
    role: ['ADMINISTRADOR', 'EDITOR'],
  };

  let mainItems;

  switch (userRole) {
    case 'DIRETORIA':
      mainItems = [dashboardItem, processosItem];
      break;
    case 'RH':
      mainItems = [frequenciaItem, diariaItem];
      break;
    case 'RH2':
      mainItems = [diariaItem];
      break;
    case 'GERENTE':
      mainItems = [dashboardItem, diariaItem, suprimentoItem, processosItem];
      break;
    case 'ADMINISTRADOR':
      mainItems = [dashboardItem, diariaItem, suprimentoItem, formItem, frequenciaItem, processosItem];
      break;
    case 'GERENTE_SETORIAL':
    case 'EDITOR_PROCESSOS':
      mainItems = [processosItem];
      break;
    default:
      mainItems = [dashboardItem, diariaItem, suprimentoItem, formItem, processosItem];
      break;
  }

  const canManageUsers = isAdminUser() || userRole === 'GERENTE' || userRole === 'GERENTE_SETORIAL';
  const gerenciarPath = (userRole === 'GERENTE' || userRole === 'GERENTE_SETORIAL') ? '/user/gerente' : '/user/gerenciador';

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header>
      <nav className="navbar-contratos">
        <div className="navbar-left-contratos">
          <button
            type="button"
            className="navbar-logo-btn-contratos"
            onClick={() => navigate('/user/dashboard')}
            aria-label="Voltar ao início"
          >
            <img src="/Bandeira_do_Pará.svg.png" alt="Bandeira do Estado do Pará" className="navbar-bandeira" />
          </button>

          <ul className="navbar-links-contratos">
            {mainItems.map((item, index) => {
              if (item.role && !item.role.includes(userRole)) return null;
              
              const isActive = location.pathname.startsWith(item.path);
              
              return (
                <li key={index}>
                  <Link
                    to={item.path}
                    className={`navbar-link-contratos${isActive ? ' navbar-link--active' : ''}`}
                    style={{ fontSize: '15px' }}
                  >
                    {item.name}
                  </Link>
                </li>
              );
            })}
            
            <li>
              <button 
                onClick={() => setIsMapModalOpen(true)}
                className="navbar-link-contratos"
                style={{ 
                  background: 'transparent', 
                  border: 'none', 
                  cursor: 'pointer', 
                  fontFamily: 'inherit', 
                  fontSize: '15px',
                  padding: '6px 12px'
                }}
              >
                Regionais
              </button>
            </li>
          </ul>
        </div>

        {children && <div className="navbar-tabs-contratos">{children}</div>}

        <div className="navbar-right-contratos">
          
          <div 
            onClick={handleToggle} 
            className={`toggle-switch ${isToggled ? 'on' : 'off'}`} 
            style={{ marginRight: '16px', flexShrink: 0 }}
            title="Alternar Tema"
          >
            <div className='toggle-switch-handle'></div>
          </div>

          <div className="navbar-user-dropdown" ref={dropdownRef}>
            <button
              type="button"
              className="navbar-logout-btn-contratos"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <span className="navbar-username-contratos" title={userName} style={{ fontSize: '15px' }}>
                {userName}
              </span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>

            {isDropdownOpen && (
              <div className="navbar-dropdown-menu">
                {canManageUsers && (
                  <button
                    type="button"
                    onClick={() => {
                      navigate(gerenciarPath);
                      setIsDropdownOpen(false);
                    }}
                    className="navbar-dropdown-item"
                    style={{ fontSize: '14px' }}
                  >
                    Gerenciar usuários
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => {
                    navigate('/user/troca-senha');
                    setIsDropdownOpen(false);
                  }}
                  className="navbar-dropdown-item"
                  style={{ fontSize: '14px' }}
                >
                  Trocar senha
                </button>

                <button
                  type="button"
                  onClick={handleSignOut}
                  className="navbar-dropdown-item navbar-dropdown-item--danger"
                  style={{ fontSize: '14px' }}
                >
                  Sair
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {isMapModalOpen && <Suspense fallback={null}><MapModal onClose={() => setIsMapModalOpen(false)} /></Suspense>}
    </header>
  );
};

export default Navbar;